import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PlusCircle, RefreshCcw, CalendarDays, Clock3, Pencil, Check, X } from 'lucide-react';
import authFetch from '../utils/authFetch';
import './TimeTrackingPage.css';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

const todayIso = () => new Date().toISOString().split('T')[0];
const formatEuropeanDate = (value) => {
  if (!value) return '-';
  const raw = String(value);
  const isoDateMatch = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return `${Number(isoDateMatch[3])}.${Number(isoDateMatch[2])}.${isoDateMatch[1]}`;
  }
  const euroDateMatch = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (euroDateMatch) {
    return `${Number(euroDateMatch[1])}.${Number(euroDateMatch[2])}.${euroDateMatch[3]}`;
  }
  return raw;
};
const formatEur = (value) => `${Number(value || 0).toFixed(2)} €`;
const getMonthKey = (value) => {
  const raw = String(value || '');
  const match = raw.match(/^(\d{4})-(\d{2})/);
  if (match) {
    return `${match[1]}-${match[2]}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};
const formatMonthLabel = (monthKey) => {
  if (!monthKey) return 'Unknown Month';
  const [year, month] = monthKey.split('-');
  const parsed = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(parsed.getTime())) {
    return monthKey;
  }
  return parsed.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const TimeTrackingPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ totalHours: 0, totalMinutes: 0, monthEarningsEur: 0, currentHourlyRateEur: null });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editForm, setEditForm] = useState({ workDate: '', startTime: '', endTime: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [form, setForm] = useState({
    workDate: todayIso(),
    startTime: '08:00',
    endTime: '16:00',
    notes: ''
  });

  const loadEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/my?limit=200`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load time entries');
      }
      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setTotals(payload.totals || { totalHours: 0, totalMinutes: 0, monthEarningsEur: 0, currentHourlyRateEur: null });
    } catch (err) {
      setError(err.message || 'Failed to load time entries');
      setEntries([]);
      setTotals({ totalHours: 0, totalMinutes: 0, monthEarningsEur: 0, currentHourlyRateEur: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const liveHours = useMemo(() => {
    const start = form.startTime.split(':').map(Number);
    const end = form.endTime.split(':').map(Number);
    if (start.length !== 2 || end.length !== 2 || Number.isNaN(start[0]) || Number.isNaN(end[0])) {
      return '-';
    }
    const minutes = (end[0] * 60 + end[1]) - (start[0] * 60 + start[1]);
    if (!Number.isFinite(minutes) || minutes <= 0) return '-';
    return (minutes / 60).toFixed(2);
  }, [form.startTime, form.endTime]);

  const monthGroups = useMemo(() => {
    const grouped = {};
    entries.forEach((entry) => {
      const key = getMonthKey(entry.workDate) || 'unknown';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(entry);
    });

    const currentMonthKey = todayIso().slice(0, 7);
    const currentMonthEntries = grouped[currentMonthKey] || [];
    const previousMonthKeys = Object.keys(grouped)
      .filter((key) => key !== currentMonthKey)
      .sort((a, b) => b.localeCompare(a));

    return {
      currentMonthKey,
      currentMonthEntries,
      previousMonths: previousMonthKeys.map((key) => ({ key, entries: grouped[key] }))
    };
  }, [entries]);

  const getTableTotalEarnings = (tableEntries) => tableEntries.reduce((sum, entry) => sum + (Number(entry.earningsEur) || 0), 0);

  const startEditEntry = (entry) => {
    setError('');
    setEditingEntryId(entry.id);
    setEditForm({
      workDate: entry.workDate || '',
      startTime: entry.startTime || '',
      endTime: entry.endTime || '',
      notes: entry.notes || ''
    });
  };

  const cancelEditEntry = () => {
    setEditingEntryId(null);
    setEditForm({ workDate: '', startTime: '', endTime: '', notes: '' });
  };

  const saveEditEntry = async (entryId) => {
    setSavingEdit(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/entries/${entryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to update time entry');
      }

      cancelEditEntry();
      await loadEntries();
    } catch (err) {
      setError(err.message || 'Failed to update time entry');
    } finally {
      setSavingEdit(false);
    }
  };

  const renderEntryRow = (entry, keyPrefix = '') => {
    const isEditing = editingEntryId === entry.id;
    return (
      <tr key={`${keyPrefix}${entry.id}`}>
        <td>
          {isEditing ? (
            <input
              type="date"
              value={editForm.workDate}
              onChange={(event) => setEditForm((prev) => ({ ...prev, workDate: event.target.value }))}
              className="tt-edit-input"
            />
          ) : formatEuropeanDate(entry.workDate)}
        </td>
        <td>
          {isEditing ? (
            <input
              type="time"
              value={editForm.startTime}
              onChange={(event) => setEditForm((prev) => ({ ...prev, startTime: event.target.value }))}
              className="tt-edit-input"
            />
          ) : entry.startTime}
        </td>
        <td>
          {isEditing ? (
            <input
              type="time"
              value={editForm.endTime}
              onChange={(event) => setEditForm((prev) => ({ ...prev, endTime: event.target.value }))}
              className="tt-edit-input"
            />
          ) : entry.endTime}
        </td>
        <td>{Number(entry.hoursWorked || 0).toFixed(2)}</td>
        <td>{entry.earningsEur === null || entry.earningsEur === undefined ? '-' : formatEur(entry.earningsEur)}</td>
        <td>
          {isEditing ? (
            <input
              type="text"
              value={editForm.notes}
              onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
              className="tt-edit-input"
            />
          ) : (entry.notes || '-')}
        </td>
        <td>
          {isEditing ? (
            <div className="tt-row-actions">
              <button onClick={() => saveEditEntry(entry.id)} disabled={savingEdit} title="Save" className="tt-icon-btn tt-icon-btn-save">
                <Check size={14} />
              </button>
              <button onClick={cancelEditEntry} disabled={savingEdit} title="Cancel" className="tt-icon-btn tt-icon-btn-cancel">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button onClick={() => startEditEntry(entry)} title="Edit entry" className="tt-icon-btn">
              <Pencil size={14} />
            </button>
          )}
        </td>
      </tr>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await authFetch(`${API_BASE}/api/my-time/time-tracking/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workDate: form.workDate,
          startTime: form.startTime,
          endTime: form.endTime,
          notes: form.notes
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to save time entry');
      }

      setMessage('Time entry saved.');
      setForm((prev) => ({ ...prev, notes: '' }));
      await loadEntries();
    } catch (err) {
      setError(err.message || 'Failed to save time entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tt-page">
      <button
        onClick={() => navigate('/my-time')}
        className="tt-back-btn"
      >
        <ArrowLeft size={16} /> Back to My Time
      </button>

      <div className="tt-header-row">
        <div>
          <h1 className="tt-title">Time Tracking</h1>
          <p className="tt-subtitle">Track your workday and keep a running total.</p>
        </div>
        <button
          onClick={loadEntries}
          className="tt-ghost-btn"
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {message && <div className="tt-alert tt-alert-success">{message}</div>}
      {error && <div className="tt-alert tt-alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="tt-form-card">
        <div className="tt-form-grid">
          <div>
            <label className="tt-label">Work Date</label>
            <input type="date" value={form.workDate} onChange={(e) => setForm((prev) => ({ ...prev, workDate: e.target.value }))} className="tt-input" required />
          </div>
          <div>
            <label className="tt-label">Start Time</label>
            <input type="time" value={form.startTime} onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))} className="tt-input" required />
          </div>
          <div>
            <label className="tt-label">End Time</label>
            <input type="time" value={form.endTime} onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))} className="tt-input" required />
          </div>
          <div>
            <label className="tt-label">Hours (live)</label>
            <div className="tt-live-hours">{liveHours === '-' ? 'Set start and end time' : `${liveHours} h`}</div>
          </div>
        </div>
        <div className="tt-notes-wrap">
          <label className="tt-label">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="What did you work on today?" className="tt-input tt-textarea" />
        </div>
        <div className="tt-form-footer">
          <span className="tt-inline-hint">Hours are calculated automatically from start and end time.</span>
          <button type="submit" disabled={submitting} className="tt-primary-btn">
            <PlusCircle size={16} /> {submitting ? 'Saving...' : 'Add Entry'}
          </button>
        </div>
      </form>

      <div className="tt-metrics-row">
        <div className="tt-metric-card">
          <CalendarDays size={16} />
          <span>Total entries</span>
          <strong>{entries.length}</strong>
        </div>
        <div className="tt-metric-card">
          <Clock3 size={16} />
          <span>Total hours</span>
          <strong>{Number(totals.totalHours || 0).toFixed(2)}</strong>
        </div>
        <div className="tt-metric-card">
          <Clock3 size={16} />
          <span>This Month Earnings</span>
          <strong>{Number(totals.monthEarningsEur || 0).toFixed(2)} €</strong>
        </div>
        <div className="tt-metric-card">
          <Clock3 size={16} />
          <span>Current €/h</span>
          <strong>{totals.currentHourlyRateEur === null || totals.currentHourlyRateEur === undefined ? '-' : `${Number(totals.currentHourlyRateEur).toFixed(2)} €`}</strong>
        </div>
      </div>

      <div className="tt-table-card">
        <div className="tt-table-title-row">
          <div className="tt-table-title">{formatMonthLabel(monthGroups.currentMonthKey)} ({monthGroups.currentMonthEntries.length})</div>
        </div>

        {loading ? (
          <div className="tt-empty">Loading entries...</div>
        ) : monthGroups.currentMonthEntries.length === 0 ? (
          <div className="tt-empty">No entries for this month.</div>
        ) : (
          <div className="tt-table-wrap">
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Hours</th>
                  <th>Earnings</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {monthGroups.currentMonthEntries.map((entry) => renderEntryRow(entry))}
              </tbody>
              <tfoot>
                <tr className="tt-table-footer-row">
                  <td colSpan={4}>Month to date earnings</td>
                  <td>{formatEur(totals.monthEarningsEur)}</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {!loading && monthGroups.previousMonths.map((monthGroup) => (
        <div className="tt-table-card" key={monthGroup.key}>
          <div className="tt-table-title-row">
            <div className="tt-table-title">{formatMonthLabel(monthGroup.key)} ({monthGroup.entries.length})</div>
            <div className="tt-inline-hint">Total earnings: {formatEur(getTableTotalEarnings(monthGroup.entries))}</div>
          </div>
          <div className="tt-table-wrap">
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Hours</th>
                  <th>Earnings</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {monthGroup.entries.map((entry) => renderEntryRow(entry, `month-${monthGroup.key}-`))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TimeTrackingPage;
